import PropTypes from 'prop-types';
import WaterfallChart from '@/components/charts/WaterfallChart';
import { parseTEAForWaterfall } from './waterfallUtils';

/**
 * Renders a TEA result card.
 *
 * compact=true  → chat bubble: hero number only (no chart)
 * compact=false → workspace:   hero + WaterfallChart cost breakdown
 *
 * Data shape (from get_tea_results):
 *   { total, unit, breakdown: [{label, cost}] }
 *
 * The agent breakdown matches parseTEAGraphData's expected format exactly.
 */

function TEAResultCard({ data, compact = true }) {
  const { total, unit, breakdown } = data;

  const card = (
    <div className={`bg-white border border-stone-200 rounded-md p-4 space-y-3 ${compact ? 'w-full max-w-[85%]' : 'w-full'}`}>
      {/* Hero */}
      <div>
        <div className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
          Levelized Cost
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className={`font-bold text-slate-900 ${compact ? 'text-2xl' : 'text-4xl'}`}>{total.toFixed(2)}</span>
          <span className={`text-slate-400 ${compact ? 'text-sm' : 'text-base'}`}>{unit}</span>
        </div>
      </div>

      {/* Waterfall (workspace only) */}
      {!compact && breakdown?.length > 0 && (
        <div className="pt-2 border-t border-stone-100">
          <div className="text-[10px] text-slate-400 uppercase tracking-wide mb-2">
            Cost breakdown
          </div>
          <WaterfallChart
            data={parseTEAForWaterfall(breakdown)}
            label="Levelized Cost"
            yAxisLabel={unit}
          />
        </div>
      )}
    </div>
  );

  if (!compact) return card;
  return (
    <div className="flex gap-2.5 items-start">
      <div className="w-6 shrink-0" />
      {card}
    </div>
  );
}

TEAResultCard.propTypes = {
  compact: PropTypes.bool,
  data: PropTypes.shape({
    total: PropTypes.number.isRequired,
    unit: PropTypes.string.isRequired,
    breakdown: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      cost: PropTypes.number,
    })),
  }).isRequired,
};

export default TEAResultCard;
